import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Users, Phone, Mail, MessageCircle, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, telHref, waHref, mailHref } from "@/lib/format";
import { EmptyState } from "@/components/EmptyState";
import { ContactFormDialog } from "@/components/ContactFormDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const TYPE_COLOR = { "Decision Maker": "#E31E24", "Purchase": "#0057A8", "Project Manager": "#7C3AED", "Consultant": "#0284C7", "Engineer": "#059669", "Contractor": "#EA580C", "Other": "#64748B" };

export default function Contacts() {
  const { meta } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 200 });
    if (search) params.set("search", search);
    if (type) params.set("contact_type", type);
    const { data } = await api.get(`/contacts?${params}`);
    setItems(data.items); setLoading(false);
  }, [search, type]);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => { await api.delete(`/contacts/${confirm.id}`); toast.success("Contact deleted"); setConfirm(null); load(); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Contacts</h1><p className="mt-0.5 text-sm text-slate-500">{items.length} contacts</p></div>
        <Button data-testid="new-contact-button" onClick={() => { setEdit(null); setFormOpen(true); }} className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Add Contact</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="contacts-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email, company…" className="h-10 pl-9" />
        </div>
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="contacts-filter-type" className="h-10 w-48"><SelectValue placeholder="Contact type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{(meta?.contact_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="contacts-empty" icon={Users} title="No contacts yet" description="Add key people from your customer companies." action={<Button onClick={() => setFormOpen(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Add Contact</Button>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`contact-card-${c.id}`} className="group rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:border-slate-300 hover:shadow-md">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: TYPE_COLOR[c.contact_type] || "#64748B" }}>{initials(c.name)}</div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-base font-semibold text-slate-800">{c.name}</h3>
                  <p className="truncate text-xs text-slate-500">{c.designation || "—"}{c.company_name && <> · {c.company_name}</>}</p>
                  <span className="mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: `${TYPE_COLOR[c.contact_type]}14`, color: TYPE_COLOR[c.contact_type] }}>{c.contact_type}</span>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-3">
                <div className="flex items-center gap-1">
                  {c.phone && <a href={telHref(c.phone)} className="rounded-md p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600" title={c.phone}><Phone className="h-4 w-4" /></a>}
                  {(c.whatsapp || c.phone) && <a href={waHref(c.whatsapp || c.phone)} target="_blank" rel="noreferrer" className="rounded-md p-1.5 text-slate-400 hover:bg-green-50 hover:text-green-600"><MessageCircle className="h-4 w-4" /></a>}
                  {c.email && <a href={mailHref(c.email)} className="rounded-md p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600" title={c.email}><Mail className="h-4 w-4" /></a>}
                </div>
                <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                  <button onClick={() => { setEdit(c); setFormOpen(true); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setConfirm(c)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContactFormDialog open={formOpen} onOpenChange={setFormOpen} contact={edit} onSaved={load} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Delete contact?" description="This will permanently remove the contact." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}
